// src/pages/RecycleBin.js
import React, { useState, useEffect } from "react";
import { useAuth } from "../hooks/useAuth";

export default function RecycleBin() {
  const { user }    = useAuth();
  const [courses,   setCourses]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [working,   setWorking]   = useState(null); // courseId being actioned

  const [error,     setError]     = useState(null);

  async function load() {
    setLoading(true); setError(null);
    try {
      const res  = await fetch("/api/coursebin");
      if (!res.ok) { const d=await res.json(); throw new Error(d.error||`HTTP ${res.status}`); }
      const data = await res.json();
      setCourses(data.courses || []);
    } catch(e) { setError(e.message); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function handleRestore(course) {
    setWorking(course.id);
    try {
      const res  = await fetch(`/api/coursebin/${course.id}/restore`, { method:"POST" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setCourses(prev => prev.filter(c => c.id !== course.id));
    } catch (e) { alert(e.message); }
    finally { setWorking(null); }
  }

  async function handleDelete(course) {
    if (!window.confirm(`Permanently delete "${course.name}"? This cannot be undone and will remove all associated data.`)) return;
    setWorking(course.id);
    try {
      const res  = await fetch(`/api/coursebin/${course.id}`, { method:"DELETE" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setCourses(prev => prev.filter(c => c.id !== course.id));
    } catch (e) { alert(e.message); }
    finally { setWorking(null); }
  }

  if (user?.role !== "admin") return <div className="card"><p>Admin access required.</p></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">🗑 Course Recycle Bin</div>
          <div className="page-subtitle">Deleted courses are kept here indefinitely until permanently removed</div>
        </div>
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        {loading ? (
          <div className="page-loading"><span className="spinner" style={{ width: 24, height: 24 }}/></div>
        ) : error ? (
          <div className="empty-state"><h3>Failed to load</h3><p style={{color:"var(--red)"}}>{error}</p><button className="btn-secondary" style={{marginTop:12}} onClick={load}>Retry</button></div>
        ) : courses.length === 0 ? (
          <div className="empty-state">
            <div style={{ fontSize: 40, marginBottom: 12 }}>🗑</div>
            <h3>Recycle bin is empty</h3>
            <p>Deleted courses will appear here.</p>
          </div>
        ) : (
          <table className="contacts-table">
            <thead>
              <tr>
                <th>Course</th>
                <th>Version</th>
                <th>Status at deletion</th>
                <th>Deleted</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {courses.map(course => (
                <tr key={course.id} className="contact-row">
                  <td className="contact-cell">
                    <div className="contact-name">{course.name}</div>
                    {course.description && (
                      <div className="contact-jobtitle">{course.description}</div>
                    )}
                  </td>
                  <td className="contact-cell" style={{ fontSize: 13, color: "var(--gray-500)" }}>
                    {course.version ? `v${course.version}` : "—"}
                  </td>
                  <td className="contact-cell">
                    {course.status === "live"     && <span className="badge badge-ok">Was Live</span>}
                    {course.status === "retired"  && <span className="badge badge-due">Was Retired</span>}
                    {(!course.status || course.status === "draft") && <span className="badge badge-neutral">Was Draft</span>}
                  </td>
                  <td className="contact-cell" style={{ fontSize: 13, color: "var(--gray-500)" }}>
                    {course.deletedAt ? new Date(course.deletedAt).toLocaleDateString("en-GB", { day:"numeric", month:"short", year:"numeric" }) : "—"}
                  </td>
                  <td className="contact-cell">
                    <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                      <button
                        className="btn-secondary"
                        style={{ fontSize: 12, padding: "4px 12px" }}
                        onClick={() => handleRestore(course)}
                        disabled={working === course.id}
                      >
                        {working === course.id ? "…" : "↩ Restore"}
                      </button>
                      <button
                        className="btn-ghost"
                        style={{ fontSize: 12, padding: "4px 10px", color: "var(--red)" }}
                        onClick={() => handleDelete(course)}
                        disabled={working === course.id}
                      >
                        Delete Forever
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {courses.length > 0 && (
        <p style={{ fontSize: 12, color: "var(--gray-400)", marginTop: 12, textAlign: "center" }}>
          Restoring a course sets it back to Draft status. Training history is preserved.
        </p>
      )}
    </div>
  );
}
